/**
 * Session Storage Utilities
 * Centralized session management for chat persistence
 */

export interface SessionStorageData {
  sessionId: string;
  timestamp: number;
  isActive: boolean;
}

const SESSION_KEY = 'chat_session_id';
const SESSION_DATA_KEY = 'chat_session_data';
const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours

export const sessionStorage = {
  /**
   * Store session ID with metadata
   */
  storeSession: (sessionId: string): void => {
    try {
      const sessionData: SessionStorageData = {
        sessionId,
        timestamp: Date.now(),
        isActive: true
      };
      
      localStorage.setItem(SESSION_KEY, sessionId);
      localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
      
      console.log('Session stored:', sessionId);
    } catch (error) {
      console.error('Failed to store session:', error);
    }
  },

  /**
   * Retrieve session ID if valid
   */
  retrieveSession: (): string | null => {
    try {
      const sessionId = localStorage.getItem(SESSION_KEY);
      const sessionDataStr = localStorage.getItem(SESSION_DATA_KEY);
      
      if (!sessionId || !sessionDataStr) {
        return null;
      }
      
      const sessionData: SessionStorageData = JSON.parse(sessionDataStr);
      
      // Check if session is expired
      if (Date.now() - sessionData.timestamp > SESSION_TIMEOUT) {
        console.log('Session expired, clearing');
        sessionStorage.clearSession();
        return null;
      }
      
      // Check if session is marked as inactive
      if (!sessionData.isActive) {
        console.log('Session marked inactive, clearing');
        sessionStorage.clearSession();
        return null;
      }
      
      console.log('Session retrieved:', sessionId);
      return sessionId;
    } catch (error) {
      console.error('Failed to retrieve session:', error);
      sessionStorage.clearSession();
      return null;
    }
  },

  /**
   * Clear session data
   */
  clearSession: (): void => {
    try {
      localStorage.removeItem(SESSION_KEY);
      localStorage.removeItem(SESSION_DATA_KEY);
      console.log('Session cleared');
    } catch (error) {
      console.error('Failed to clear session:', error);
    }
  },

  /**
   * Mark session as inactive (for logout, etc.)
   */
  markSessionInactive: (): void => {
    try {
      const sessionDataStr = localStorage.getItem(SESSION_DATA_KEY);
      
      if (sessionDataStr) {
        const sessionData: SessionStorageData = JSON.parse(sessionDataStr);
        sessionData.isActive = false;
        
        localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
        console.log('Session marked inactive');
      }
    } catch (error) {
      console.error('Failed to mark session inactive:', error);
    }
  },

  /**
   * Check if session exists and is valid
   */
  hasValidSession: (): boolean => {
    return sessionStorage.retrieveSession() !== null;
  },

  /**
   * Update session timestamp (for activity tracking)
   */
  updateSessionActivity: (): void => {
    try {
      const sessionDataStr = localStorage.getItem(SESSION_DATA_KEY);
      
      if (sessionDataStr) {
        const sessionData: SessionStorageData = JSON.parse(sessionDataStr);
        sessionData.timestamp = Date.now();
        
        localStorage.setItem(SESSION_DATA_KEY, JSON.stringify(sessionData));
      }
    } catch (error) {
      console.error('Failed to update session activity:', error);
    }
  }
};